import type { OperationsArrivalDayDto } from "@/api/endpoints";
import { InfoTooltip } from "@/components/InfoTooltip";
import { UnlensedTag } from "./LensTags";

interface Props {
  days: OperationsArrivalDayDto[];
  /** A lens is on elsewhere: this card keeps the full population and says so. */
  unlensed?: boolean;
  onDrill: (day: string, kind: "arrival" | "departure") => void;
}

const TIP =
  "Requests created and requests completed per business day over the last 10 business days. Does not follow the period selector.";

const W = 300;
const H = 150;
const PAD = 8;

/** Panel 6: arrivals against departures over the last ten business days. */
export function ArrivalsLine({ days, unlensed, onDrill }: Props) {
  const n = days.length;
  const max = Math.max(1, ...days.flatMap((d) => [d.arrivals, d.departures]));
  const yTop = Math.max(4, Math.ceil(max / 2) * 2);
  const x = (i: number) => (n > 1 ? PAD + (i * (W - 2 * PAD)) / (n - 1) : W / 2);
  const y = (v: number) => H - PAD - (v / yTop) * (H - 2 * PAD);
  const path = (key: "arrivals" | "departures") =>
    days.map((d, i) => `${i ? "L" : "M"}${x(i).toFixed(1)},${y(d[key]).toFixed(1)}`).join(" ");
  const net = days.reduce((s, d) => s + d.arrivals - d.departures, 0);
  const dayLabel = (iso: string) => {
    const d = new Date(`${iso}T12:00:00Z`);
    return `${["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"][d.getUTCDay()]} ${d.getUTCDate()}`;
  };

  return (
    <section className={`ops-panel ops-span-4${unlensed ? " ops-unlensed" : ""}`} aria-labelledby="ops-arr-h">
      <h3 id="ops-arr-h">
        Arrivals against departures
        <span className="sub">10 business days</span>
        <InfoTooltip inline text={TIP} />
        {unlensed && <UnlensedTag />}
        <span className={`ops-net ${net > 0 ? "up" : net < 0 ? "down" : "flat"}`}>{net > 0 ? `+${net} ↑` : net < 0 ? `${net} ↓` : "0 →"}</span>
      </h3>
      <div className="ops-lines">
        <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" role="img" aria-label="Arrivals against departures">
          {[0, 0.5, 1].map((f) => (
            <line key={f} x1={PAD} x2={W - PAD} y1={y(yTop * f).toFixed(1)} y2={y(yTop * f).toFixed(1)} stroke="var(--border-subtle)" strokeDasharray="3 3" />
          ))}
          <text x={PAD} y={(y(yTop) - 2).toFixed(1)} fontSize="9" fill="#8e98a8">{yTop}</text>
          <path d={path("departures")} fill="none" stroke="var(--gw-blue-300)" strokeWidth="2" vectorEffect="non-scaling-stroke" />
          <path d={path("arrivals")} fill="none" stroke="var(--gw-blue-500)" strokeWidth="2" vectorEffect="non-scaling-stroke" />
          {days.map((d, i) => (
            <g key={d.day}>
              <circle cx={x(i).toFixed(1)} cy={y(d.departures).toFixed(1)} r="2.6" fill="var(--gw-blue-300)" style={{ cursor: "pointer" }} onClick={() => onDrill(d.day, "departure")}>
                <title>{dayLabel(d.day)}: {d.departures} departures</title>
              </circle>
              <circle cx={x(i).toFixed(1)} cy={y(d.arrivals).toFixed(1)} r="2.6" fill="var(--gw-blue-500)" style={{ cursor: "pointer" }} onClick={() => onDrill(d.day, "arrival")}>
                <title>{dayLabel(d.day)}: {d.arrivals} arrivals</title>
              </circle>
            </g>
          ))}
        </svg>
        <div className="xax">
          {days.map((d, i) => (
            <span key={d.day} title={dayLabel(d.day)}>{i === n - 1 ? "D" : `D-${n - 1 - i}`}</span>
          ))}
        </div>
      </div>
      <div className="ops-legend" style={{ marginTop: 6 }}>
        <span><i className="ops-sw" style={{ background: "var(--gw-blue-500)" }} />Arrivals (created)</span>
        <span><i className="ops-sw" style={{ background: "var(--gw-blue-300)" }} />Departures (completed)</span>
      </div>
    </section>
  );
}
